import { useState, useCallback } from "react";
import { Modal, formatINR } from "../../../Component/Workshop/UI";
 
// ─── Form default ─────────────────────────────────────────────────────────────
export const AMOUNT_FORM_DEFAULT = {
  // Step 1 — invoice
  taxableAmount:     "",   // without GST
  gstPercent:    "18", // default GST percent, editable in case of different GST slabs
  gstAmount:         "",   // auto = taxable * 0.18, editable
  towingAmount:      "",
  totalInvoice:      "",   // auto = taxable + gst + towing — also becomes estimateAmount
 
  // Step 2 — deductions
  depreciation:      "",
  imt23:             "",
  consumable:        "",
  salvage:           "",
  fileCharge:        "",
  costToCustomer:    "",   // auto = sum of deductions, editable
 
  // Step 3 — discount & partial
  workshopDiscount:  "0",
  netAfterDiscount:  "",   // auto = assessed - discount, editable
  partialApproval:   false,
  insApprovedAmount: "",   // only when partialApproval = true
  shortfallAmount:   "",   // auto = netAfterDiscount - insApproved, editable
 
  // misc
  note: "",
};
 
// ─── Open helper (call this instead of inline setState) ───────────────────────
export function openAmountModal(claim, setAmountModal, setAmountForm) {
  setAmountModal(claim);
  if (claim.taxableAmount != null) {
    setAmountForm({
      taxableAmount:     String(claim.taxableAmount     ?? ""),
      gstPercent:        String(claim.gstRate ?? "18"), 
      gstAmount:         String(claim.gstAmount         ?? ""),
      towingAmount:      String(claim.towingAmount      ?? ""),
      totalInvoice:      String(claim.totalInvoice      ?? ""),
      depreciation:      String(claim.depreciation      ?? ""),
      imt23:             String(claim.imt23             ?? ""),
      consumable:        String(claim.consumable        ?? ""),
      salvage:           String(claim.salvage           ?? ""),
      fileCharge:        String(claim.fileCharge        ?? ""),
      costToCustomer:    String(claim.costToCustomer    ?? ""),
      workshopDiscount:  String(claim.workshopDiscount  ?? "0"),
      netAfterDiscount:  String(claim.netAfterDiscount  ?? ""),
      partialApproval:   !!claim.insApprovedAmount,
      insApprovedAmount: String(claim.insApprovedAmount ?? ""),
      shortfallAmount:   String(claim.customerShortfall    ?? ""),
      note: "",
    });
  } else {
    setAmountForm({
      ...AMOUNT_FORM_DEFAULT,
      workshopDiscount: String(claim.workshopDiscount ?? "0"),
    });
  }
}

// ─── Step config ──────────────────────────────────────────────────────────────
const STEPS = [
  { id: 1, label: "Invoice"    },
  { id: 2, label: "Deductions" },
  { id: 3, label: "Discount"   },
  { id: 4, label: "Review"     },
];
 
// ─── Main component ───────────────────────────────────────────────────────────
export default function AmountModal({ claim, form, setForm, onClose, onSave, isPending }) {
  console.log("Rendering AmountModal with claim:", form);
  const [step, setStep] = useState(1);
 
  const setF = useCallback((key, val) => setForm((f) => ({ ...f, [key]: val })), [setForm]);
 
  if (!claim) return null;
 
  // ── Derived live values ────────────────────────────────────────────────────
  const num = (v) => parseFloat(v) || 0;
 
  const taxable      = num(form.taxableAmount);
  const gstPercent  = parseFloat(form.gstPercent) || 18;
  const autoGst     = Math.round(taxable * (gstPercent / 100));
  const gst          = form.gstAmount         !== "" ? num(form.gstAmount)         : autoGst;
  const towing       = num(form.towingAmount);
  const autoInvoice  = taxable + gst + towing;
  const totalInvoice = form.totalInvoice       !== "" ? num(form.totalInvoice)       : autoInvoice;
 
  const dep          = num(form.depreciation);
  const imt23        = num(form.imt23);
  const consumable   = num(form.consumable);
  const salvage      = num(form.salvage);
  const fileCharge   = num(form.fileCharge);
  const autoCost     = dep + imt23 + consumable + salvage + fileCharge;
  const costToCustomer = form.costToCustomer   !== "" ? num(form.costToCustomer)   : autoCost;
 
  const assessed       = taxable - costToCustomer;
  const wDiscount      = num(form.workshopDiscount);
  const autoNet        = Math.max(0, totalInvoice - wDiscount);
  const netAfter       = form.netAfterDiscount !== "" ? num(form.netAfterDiscount) : autoNet;
 
  const partialOpen    = form.partialApproval;
  const insApproved    = num(form.insApprovedAmount);
  const autoShortfall  = partialOpen ? Math.max(0, assessed - insApproved) : 0;
  const shortfall      = form.shortfallAmount  !== "" ? num(form.shortfallAmount)  : autoShortfall;
 
  const approvedAmt    = partialOpen ? insApproved : assessed;
  const customerDue    = Math.max(0, totalInvoice - approvedAmt - wDiscount);
 
  // ── Helpers ────────────────────────────────────────────────────────────────
  const fmt = (v) => {
    if (v == null || v === "" || isNaN(v)) return "—";
    if (v === 0) return "NIL";
    return formatINR(v);
  };
 
  const canProceed = () => {
    if (step === 1) return !!form.taxableAmount;
    if (step === 2) return true; // deductions optional
    if (step === 3) return !partialOpen || !!form.insApprovedAmount;
    return true;
  };
 
  const handleClose = () => { setStep(1); onClose(); };
 
  // ── Step content ───────────────────────────────────────────────────────────
  const renderStep = () => {
    switch (step) {
      case 1: return <Step1 form={form} setF={setF} gstPercent={gstPercent} autoGst={autoGst} autoInvoice={autoInvoice} fmt={fmt} />;
      case 2: return <Step2 form={form} setF={setF} autoCost={autoCost} taxable={taxable} costToCustomer={costToCustomer} assessed={assessed} fmt={fmt} />;
      case 3: return <Step3 form={form} setF={setF} setForm={setForm} netAfter={netAfter} autoNet={autoNet} insApproved={insApproved} autoShortfall={autoShortfall} shortfall={shortfall} assessed={assessed} fmt={fmt} />;
      case 4: return <Step4 form={form} setF={setF} totalInvoice={totalInvoice} taxable={taxable} gst={gst} towing={towing} costToCustomer={costToCustomer} assessed={assessed} wDiscount={wDiscount} netAfter={netAfter} partialOpen={partialOpen} insApproved={insApproved} shortfall={shortfall} customerDue={customerDue} fmt={fmt} />;
      default: return null;
    }
  };
 
  return (
    <Modal
      open={!!claim}
      onClose={handleClose}
      title="Enter approved amount & cost breakdown"
      maxWidth={580}
      footer={
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
          {/* Step dots */}
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {STEPS.map((s) => (
              <div key={s.id} style={{
                width: s.id === step ? 18 : 7,
                height: 7,
                borderRadius: 4,
                background: s.id < step  ? "var(--wd-green)"
                          : s.id === step ? "var(--wd-blue)"
                          :                 "var(--wd-border-md)",
                transition: "width .2s, background .2s",
              }} />
            ))}
          </div>
 
          {/* Buttons */}
          <div style={{ display: "flex", gap: 8 }}>
            <button className="wd-btn wd-btn-ghost" onClick={step === 1 ? handleClose : () => setStep(s => s - 1)}>
              {step === 1 ? "Cancel" : "Back"}
            </button>
            {step < 4 ? (
              <button
                className="wd-btn wd-btn-primary"
                onClick={() => setStep(s => s + 1)}
                disabled={!canProceed()}
              >
                Next
                <span style={{ marginLeft: 4, opacity: .7, fontSize: 11 }}>
                  {STEPS[step]?.label}
                </span>
              </button>
            ) : (
              <button
                className="wd-btn wd-btn-primary"
                onClick={() => { onSave(form); setStep(1); }}
                disabled={isPending || !form.taxableAmount}
              >
                {isPending ? "Saving..." : "Save amounts"}
              </button>
            )}
          </div>
        </div>
      }
    >
      {/* ── Claim reference bar ── */}
      <RefBar claim={claim} totalInvoice={totalInvoice} fmt={fmt} />
 
      {/* ── Step progress header ── */}
      <StepHeader current={step} />
 
      {/* ── Step body ── */}
      <div style={{ minHeight: 280 }}>
        {renderStep()}
      </div>
    </Modal>
  );
}
 
// ─── Ref bar ─────────────────────────────────────────────────────────────────
function RefBar({ claim, totalInvoice, fmt }) {
  return (
    <div style={{ display: "flex", gap: 20, padding: "10px 14px", background: "var(--wd-hover)", borderRadius: 10, flexWrap: "wrap", alignItems: "center" }}>
      <div>
        <div style={{ fontSize: 10, color: "var(--wd-t3)" }}>Workshop estimate</div>
        <div style={{ fontFamily: "var(--wd-mono)", fontSize: 14, fontWeight: 700, color: "var(--wd-t1)" }}>
          {totalInvoice ? fmt(totalInvoice) : fmt(claim.estimateAmount)}
        </div>
      </div>
      <div style={{ width: 1, background: "var(--wd-border)", alignSelf: "stretch" }} />
      <div>
        <div style={{ fontSize: 10, color: "var(--wd-t3)" }}>Insurer</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--wd-t1)" }}>{claim.insuranceCompany}</div>
      </div>
      {claim.claimNumber && (
        <div style={{ marginLeft: "auto" }}>
          <div style={{ fontSize: 10, color: "var(--wd-t3)" }}>Claim no.</div>
          <div style={{ fontFamily: "var(--wd-mono)", fontSize: 11, color: "var(--wd-blue)" }}>{claim.claimNumber}</div>
        </div>
      )}
    </div>
  );
}
 
// ─── Step progress header ─────────────────────────────────────────────────────
function StepHeader({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      {STEPS.map((s, i) => {
        const done   = s.id < current;
        const active = s.id === current;
        return (
          <div key={s.id} style={{ display: "flex", alignItems: "center", flex: i < STEPS.length - 1 ? 1 : "none" }}>
            {/* dot + label */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
              <div style={{
                width: 22, height: 22, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 700, flexShrink: 0,
                background: done   ? "rgba(34,197,94,.15)"
                          : active ? "rgba(59,130,246,.15)"
                          :          "var(--wd-hover)",
                color:     done   ? "var(--wd-green)"
                          : active ? "var(--wd-blue)"
                          :          "var(--wd-t3)",
                border:   active ? "2px solid rgba(59,130,246,.4)" : "none",
              }}>
                {done ? (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : s.id}
              </div>
              <div style={{
                fontSize: 10, fontWeight: active ? 600 : 400,
                color: done ? "var(--wd-green)" : active ? "var(--wd-blue)" : "var(--wd-t3)",
                whiteSpace: "nowrap",
              }}>
                {s.label}
              </div>
            </div>
            {/* connector line */}
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 1, margin: "0 4px", marginBottom: 14,
                background: done ? "rgba(34,197,94,.4)" : "var(--wd-border)",
              }} />
            )}
          </div>
        );
      })}
    </div>
  );
}
 
// ─── Step 1: Invoice ──────────────────────────────────────────────────────────
function Step1({ form, setF, gstPercent, autoGst, autoInvoice, fmt }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepIntro
        title="Base invoice amounts"
        desc="Enter the taxable amount from the workshop invoice. GST and total invoice auto-calculate."
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Taxable amount (without GST) ₹ *" hint="Workshop invoice excl. GST">
          <input className="wd-input wd-mono" type="number" placeholder="e.g. 241107"
            value={form.taxableAmount}
            onChange={e => setF("taxableAmount", e.target.value)} autoFocus />
        </Field>

        {/* GST % + amount — side by side in one field cell */}
        <Field label="GST ₹ (auto)" hint="Override % or amount independently">
          <div style={{ display: "flex", gap: 6 }}>
            {/* Percentage input */}
            <div style={{ position: "relative", width: 80, flexShrink: 0 }}>
              <input
                className="wd-input wd-mono"
                type="number"
                min="0" max="100"
                style={{ paddingRight: 22, width: "100%" }}
                value={form.gstPercent}
                placeholder="18"
                onChange={e => {
                  setF("gstPercent", e.target.value);
                  setF("gstAmount", ""); // clear manual override so auto recalculates
                }}
              />
              <span style={{
                position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                fontSize: 10, color: "var(--wd-t3)", pointerEvents: "none",
              }}>%</span>
            </div>
            {/* Amount input — auto from % unless overridden */}
            <input
              className="wd-input wd-mono"
              type="number"
              style={{
                flex: 1,
                background: form.gstAmount === "" && form.taxableAmount ? "var(--wd-hover)" : undefined,
                color:      form.gstAmount === "" && form.taxableAmount ? "var(--wd-t3)"   : undefined,
              }}
              value={form.gstAmount !== "" ? form.gstAmount : (form.taxableAmount ? autoGst : "")}
              onChange={e => setF("gstAmount", e.target.value)}
              placeholder="auto"
            />
          </div>
        </Field>

        <Field label="Towing / crane charges ₹" hint=" ">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.towingAmount}
            onChange={e => setF("towingAmount", e.target.value)} />
        </Field>
        <Field label="Total invoice ₹ (auto)" hint="= Taxable + GST + Towing">
          <input className="wd-input wd-mono" type="number"
            style={{
              background: form.totalInvoice === "" && form.taxableAmount ? "var(--wd-hover)" : undefined,
              color:      form.totalInvoice === "" && form.taxableAmount ? "var(--wd-t3)"   : undefined,
            }}
            value={form.totalInvoice !== "" ? form.totalInvoice : (form.taxableAmount ? autoInvoice : "")}
            onChange={e => setF("totalInvoice", e.target.value)}
            placeholder="auto" />
        </Field>
      </div>

      {form.taxableAmount && (
        <InfoNote color="blue">
          Total invoice of{" "}
          <strong style={{ fontFamily: "var(--wd-mono)" }}>
            {fmt(form.totalInvoice !== "" ? parseFloat(form.totalInvoice) : autoInvoice)}
          </strong>{" "}
          will be set as the workshop estimate for this case.
        </InfoNote>
      )}
    </div>
  );
}
 
// ─── Step 2: Deductions ───────────────────────────────────────────────────────
function Step2({ form, setF, autoCost, taxable, costToCustomer, assessed, fmt }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepIntro
        title="Insurance deductions"
        desc="Enter the deduction values from the assessment sheet. Cost to customer auto-sums them."
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <Field label="Depreciation ₹">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.depreciation} onChange={e => setF("depreciation", e.target.value)} />
        </Field>
        <Field label="IMT-23 ₹">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.imt23} onChange={e => setF("imt23", e.target.value)} />
        </Field>
        <Field label="Consumable ₹">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.consumable} onChange={e => setF("consumable", e.target.value)} />
        </Field>
        <Field label="Salvage ₹">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.salvage} onChange={e => setF("salvage", e.target.value)} />
        </Field>
        <Field label="File charge ₹">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.fileCharge} onChange={e => setF("fileCharge", e.target.value)} />
        </Field>
        <Field label="Cost to customer ₹" hint="Auto-sum — tap to override">
          <input className="wd-input wd-mono" type="number"
            style={{ background: form.costToCustomer === "" && autoCost > 0 ? "var(--wd-hover)" : undefined, color: form.costToCustomer === "" && autoCost > 0 ? "var(--wd-t3)" : undefined }}
            value={form.costToCustomer !== "" ? form.costToCustomer : (autoCost > 0 ? autoCost : "")}
            onChange={e => setF("costToCustomer", e.target.value)}
            placeholder="auto" />
        </Field>
      </div>
 
      {/* Live assessment result */}
      {taxable > 0 && (
        <div style={{ background: "var(--wd-hover)", borderRadius: 10, padding: "12px 14px" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
            Insurance assessment
          </div>
          <CalcRow label="Taxable amount (1)"           value={fmt(taxable)}         />
          <CalcRow label="Cost to customer / deductions (2)" value={fmt(costToCustomer)} />
          <div style={{ height: 1, background: "var(--wd-border)", margin: "6px 0" }} />
          <CalcRow label="Assessed by insurance (1 − 2)" value={fmt(assessed)} bold color="var(--wd-blue)" />
        </div>
      )}
    </div>
  );
}
 
// ─── Step 3: Discount & partial ───────────────────────────────────────────────
function Step3({ form, setF, setForm, netAfter, autoNet, insApproved, autoShortfall, shortfall, assessed, fmt }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepIntro
        title="Workshop discount & insurer approval"
        desc="Enter the discount negotiated with the workshop. If insurer approved less than assessed, toggle the partial approval option."
      />
 
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <Field label="Workshop discount negotiated ₹" hint="Amount workshop agreed to reduce">
          <input className="wd-input wd-mono" type="number" placeholder="0"
            value={form.workshopDiscount} onChange={e => setF("workshopDiscount", e.target.value)} />
        </Field>
        <Field label="Net after discount ₹ (auto)" hint="Assessed − workshop discount">
          <input className="wd-input wd-mono" type="number"
            style={{ background: form.netAfterDiscount === "" && assessed > 0 ? "var(--wd-hover)" : undefined, color: form.netAfterDiscount === "" && assessed > 0 ? "var(--wd-t3)" : undefined }}
            value={form.netAfterDiscount !== "" ? form.netAfterDiscount : (assessed > 0 ? autoNet : "")}
            onChange={e => setF("netAfterDiscount", e.target.value)}
            placeholder="auto" />
        </Field>
      </div>
 
      {/* Partial approval toggle */}
      {!form.partialApproval ? (
        <button
          className="wd-btn wd-btn-ghost wd-btn-sm"
          style={{ color: "var(--wd-orange)", borderColor: "rgba(249,115,22,.3)", fontSize: 11, alignSelf: "flex-start" }}
          onClick={() => setF("partialApproval", true)}
        >
          + Insurer approved less than assessed amount
        </button>
      ) : (
        <div style={{ background: "rgba(249,115,22,.06)", border: "1px solid rgba(249,115,22,.2)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: "var(--wd-orange)" }}>
              Partial insurer approval
            </span>
            <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ fontSize: 10, color: "var(--wd-t3)" }}
              onClick={() => setForm(f => ({ ...f, partialApproval: false, insApprovedAmount: "", shortfallAmount: "" }))}>
              Remove
            </button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label="Actual amount approved by insurer ₹ *" hint="From insurance portal / approval letter">
              <input className="wd-input wd-mono" type="number" placeholder="e.g. 110000"
                value={form.insApprovedAmount} onChange={e => setF("insApprovedAmount", e.target.value)} autoFocus />
            </Field>
            <Field label="Shortfall ₹ (auto)" hint="Net after discount − insurer approved — borne by customer">
              <input className="wd-input wd-mono" type="number"
                style={{ background: form.shortfallAmount === "" && insApproved > 0 ? "var(--wd-hover)" : undefined, color: form.shortfallAmount === "" && insApproved > 0 ? "var(--wd-t3)" : undefined }}
                value={form.shortfallAmount !== "" ? form.shortfallAmount : (insApproved > 0 ? autoShortfall : "")}
                onChange={e => setF("shortfallAmount", e.target.value)}
                placeholder="auto" />
            </Field>
          </div>
          {shortfall > 0 && (
            <InfoNote color="orange">
              <strong style={{ fontFamily: "var(--wd-mono)" }}>{fmt(shortfall)}</strong> shortfall will be added to the customer due amount.
            </InfoNote>
          )}
        </div>
      )}
    </div>
  );
}
 
// ─── Step 4: Review ───────────────────────────────────────────────────────────
function Step4({ form, setF, totalInvoice, taxable, gst, towing, costToCustomer, assessed, wDiscount, netAfter, partialOpen, insApproved, shortfall, customerDue, fmt }) {
  const isNil   = customerDue === 0 && taxable > 0;
  const isSmall = customerDue > 0 && totalInvoice && customerDue < totalInvoice * 0.15;
  const dueColor = !taxable ? "var(--wd-t3)" : isNil ? "var(--wd-green)" : isSmall ? "var(--wd-yellow)" : "var(--wd-orange)";
 
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <StepIntro
        title="Review & confirm"
        desc="Verify the full breakdown before saving. Go back to any step to make corrections."
      />
 
      {/* Invoice breakdown */}
      <Section title="Invoice">
        <ReviewRow label="Taxable amount"   value={fmt(taxable)}       />
        <ReviewRow label="GST (18%)"        value={fmt(gst)}           />
        <ReviewRow label="Towing charges"   value={fmt(towing) === "NIL" ? "—" : fmt(towing)} />
        <ReviewRow label="Total invoice"    value={fmt(totalInvoice)}  bold />
      </Section>
 
      {/* Deduction breakdown */}
      <Section title="Insurance deductions">
        <ReviewRow label="Depreciation"     value={fmt(parseFloat(form.depreciation) || 0) === "NIL" ? "—" : fmt(parseFloat(form.depreciation) || 0)} />
        <ReviewRow label="IMT-23"           value={fmt(parseFloat(form.imt23) || 0) === "NIL" ? "—" : fmt(parseFloat(form.imt23) || 0)} />
        <ReviewRow label="Consumable"       value={fmt(parseFloat(form.consumable) || 0) === "NIL" ? "—" : fmt(parseFloat(form.consumable) || 0)} />
        <ReviewRow label="Salvage"          value={fmt(parseFloat(form.salvage) || 0) === "NIL" ? "—" : fmt(parseFloat(form.salvage) || 0)} />
        <ReviewRow label="File charge"      value={fmt(parseFloat(form.fileCharge) || 0) === "NIL" ? "—" : fmt(parseFloat(form.fileCharge) || 0)} />
        <ReviewRow label="Cost to customer" value={fmt(costToCustomer)} bold />
        <ReviewRow label="Assessed by insurance (Invoice − Cost to customer)" value={fmt(assessed)} bold color="var(--wd-blue)" />
      </Section>
 
      {/* Settlement */}
      <Section title="Settlement">
        <ReviewRow label="Workshop discount"  value={wDiscount > 0 ? `− ${fmt(wDiscount)}` : "—"} />
        <ReviewRow label="Net after discount" value={fmt(netAfter)} />
        {partialOpen && insApproved > 0 && <>
          <ReviewRow label="Insurer actual approval"           value={fmt(insApproved)}  />
          <ReviewRow label="Shortfall (borne by customer)"     value={fmt(shortfall)} color="var(--wd-orange)" />
        </>}
      </Section>
 
      {/* Final customer due */}
      <div style={{
        padding: "14px 16px", borderRadius: 12,
        background: isNil ? "rgba(34,197,94,.07)" : isSmall ? "rgba(234,179,8,.07)" : "rgba(249,115,22,.07)",
        border: `1px solid ${isNil ? "rgba(34,197,94,.25)" : isSmall ? "rgba(234,179,8,.25)" : "rgba(249,115,22,.25)"}`,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginBottom: 4 }}>Customer payable amount</div>
            <div style={{ fontFamily: "var(--wd-mono)", fontSize: 26, fontWeight: 800, color: dueColor }}>
              {customerDue === 0 ? "NIL" : fmt(customerDue)}
            </div>
            <div style={{ fontSize: 11, color: "var(--wd-t3)", marginTop: 3 }}>
              {isNil ? "Fully covered by insurance" : "Customer must pay this before vehicle delivery"}
            </div>
          </div>
          <div style={{
            fontSize: 11, fontWeight: 700, padding: "4px 12px", borderRadius: 20,
            background: isNil ? "rgba(34,197,94,.12)" : isSmall ? "rgba(234,179,8,.12)" : "rgba(249,115,22,.12)",
            color: dueColor,
          }}>
            {isNil ? "Fully covered" : isSmall ? "Small balance" : "Balance due"}
          </div>
        </div>
      </div>
 
      {/* Note */}
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <label className="wd-form-label">Note (optional)</label>
        <textarea className="wd-input" rows={2} style={{ resize: "vertical" }}
          placeholder="e.g. HDFC approved ₹1,10,000. Workshop agreed to reduce by ₹5,000."
          value={form.note} onChange={e => setF("note", e.target.value)} />
      </div>
    </div>
  );
}
 
// ─── Shared sub-components ────────────────────────────────────────────────────
function StepIntro({ title, desc }) {
  return (
    <div>
      <div style={{ fontSize: 14, fontWeight: 700, color: "var(--wd-t1)", marginBottom: 3 }}>{title}</div>
      <div style={{ fontSize: 12, color: "var(--wd-t3)", lineHeight: 1.5 }}>{desc}</div>
    </div>
  );
}
 
function Field({ label, hint, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <label className="wd-form-label" style={{ marginBottom: 0 }}>{label}</label>
      {children}
      {/* Always render — keeps grid rows vertically aligned even when only some fields have hints */}
      <span style={{ fontSize: 10, color: "var(--wd-t3)", minHeight: 14, lineHeight: "14px" }}>
        {hint ?? ""}
      </span>
    </div>
  );
}
 
function CalcRow({ label, value, bold, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0" }}>
      <span style={{ fontSize: 12, color: "var(--wd-t2)", fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span style={{ fontFamily: "var(--wd-mono)", fontSize: bold ? 14 : 12, fontWeight: bold ? 700 : 500, color: color || "var(--wd-t1)" }}>
        {value}
      </span>
    </div>
  );
}
 
function Section({ title, children }) {
  return (
    <div style={{ background: "var(--wd-hover)", borderRadius: 10, padding: "10px 14px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "var(--wd-t3)", textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 8 }}>
        {title}
      </div>
      {children}
    </div>
  );
}
 
function ReviewRow({ label, value, bold, color }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid var(--wd-border)" }}>
      <span style={{ fontSize: 12, color: "var(--wd-t3)" }}>{label}</span>
      <span style={{ fontFamily: "var(--wd-mono)", fontSize: 12, fontWeight: bold ? 700 : 500, color: color || "var(--wd-t1)" }}>
        {value}
      </span>
    </div>
  );
}
 
function InfoNote({ color = "blue", children }) {
  const colors = {
    blue:   { bg: "rgba(59,130,246,.07)",  border: "rgba(59,130,246,.15)",  text: "var(--wd-blue)"   },
    orange: { bg: "rgba(249,115,22,.07)",  border: "rgba(249,115,22,.15)",  text: "var(--wd-orange)" },
    green:  { bg: "rgba(34,197,94,.07)",   border: "rgba(34,197,94,.15)",   text: "var(--wd-green)"  },
  };
  const c = colors[color] || colors.blue;
  return (
    <div style={{ display: "flex", gap: 8, padding: "9px 12px", background: c.bg, border: `1px solid ${c.border}`, borderRadius: 8, fontSize: 12, color: c.text, lineHeight: 1.5 }}>
      <span style={{ flexShrink: 0 }}>ℹ</span>
      <span>{children}</span>
    </div>
  );
}